import { ref } from 'vue'

const checkSupported = () => typeof navigator !== 'undefined' && 'wakeLock' in navigator
const isSupported = ref(checkSupported())
const isActive = ref(false)
let wakeLockSentinel: any = null
let shouldBeActive = false
let wakeLockTimeoutTimer: ReturnType<typeof setTimeout> | null = null
let activeTimerSessionId = 0
let targetExpiryTimestamp: number | null = null
let wasScreenOffDuringExpiry = false
let isVisibilityListenerAttached = false

const isCurrentRouteRecipe = (): boolean => {
  if (typeof window === 'undefined') return false
  const path = window.location.pathname || ''
  const hash = window.location.hash || ''
  return path.includes('/recipe/') || hash.includes('/recipe/')
}

const clearWakeLockTimeout = () => {
  activeTimerSessionId++
  targetExpiryTimestamp = null
  wasScreenOffDuringExpiry = false
  if (wakeLockTimeoutTimer) {
    clearTimeout(wakeLockTimeoutTimer)
    wakeLockTimeoutTimer = null
  }
}

let pendingRequestPromise: Promise<boolean> | null = null

const requestWakeLock = async (): Promise<boolean> => {
  isSupported.value = checkSupported()
  if (!isSupported.value || typeof navigator === 'undefined' || !(navigator as any).wakeLock) {
    return false
  }
  if (wakeLockSentinel && !wakeLockSentinel.released) {
    isActive.value = true
    shouldBeActive = true
    return true
  }
  if (pendingRequestPromise) {
    return pendingRequestPromise
  }
  pendingRequestPromise = (async () => {
    try {
      if (!wakeLockSentinel || wakeLockSentinel.released) {
        wakeLockSentinel = await (navigator as any).wakeLock.request('screen')
        isActive.value = true
        shouldBeActive = true

        wakeLockSentinel.addEventListener('release', () => {
          isActive.value = false
        })
      }
      ensureVisibilityListener()
      return true
    } catch (err) {
      console.warn('Wake Lock request failed:', err)
      isActive.value = false
      return false
    } finally {
      pendingRequestPromise = null
    }
  })()
  return pendingRequestPromise
}

const releaseWakeLock = async (): Promise<void> => {
  shouldBeActive = false
  clearWakeLockTimeout()
  if (wakeLockSentinel && !wakeLockSentinel.released) {
    try {
      await wakeLockSentinel.release()
    } catch (err) {
      console.warn('Wake Lock release error:', err)
    } finally {
      wakeLockSentinel = null
      isActive.value = false
    }
  } else {
    isActive.value = false
  }
}

const scheduleWakeLockTimeout = (minutes: number, onTimeout?: () => void) => {
  clearWakeLockTimeout()
  const sessionId = ++activeTimerSessionId
  const ms = Math.max(1, minutes) * 60 * 1000
  targetExpiryTimestamp = Date.now() + ms
  wasScreenOffDuringExpiry = false

  wakeLockTimeoutTimer = setTimeout(async () => {
    // If sessionId changed or timer was cleared, abort immediately
    if (sessionId !== activeTimerSessionId) return
    wakeLockTimeoutTimer = null
    const scheduledExpiry = targetExpiryTimestamp
    targetExpiryTimestamp = null

    // If user is no longer on a recipe page, release silently without toast
    if (!isCurrentRouteRecipe()) {
      await releaseWakeLock()
      return
    }

    // If screen/phone is locked or tab is hidden, screen was turned off by user
    // Release silently so waking the phone later does not show stacked toasts
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      await releaseWakeLock()
      return
    }

    // If screen was off/hidden when the timer expired, or if timer fired with lag
    // (> 5s after scheduled expiry) due to device being suspended/locked
    if (wasScreenOffDuringExpiry || (scheduledExpiry !== null && Date.now() - scheduledExpiry > 5000)) {
      await releaseWakeLock()
      return
    }

    await releaseWakeLock()
    if (onTimeout) {
      onTimeout()
    }
  }, ms)
}

const handleVisibilityChange = async () => {
  if (typeof document === 'undefined') return
  if (document.visibilityState === 'visible') {
    // If a timeout was scheduled and its target expiration already passed while screen was off/hidden
    if (targetExpiryTimestamp !== null && Date.now() >= targetExpiryTimestamp) {
      wasScreenOffDuringExpiry = true
      shouldBeActive = false
      clearWakeLockTimeout()
      await releaseWakeLock()
      return
    }

    // Only re-acquire wake lock if shouldBeActive is true AND we are actually on a recipe page
    if (shouldBeActive && isCurrentRouteRecipe()) {
      await requestWakeLock()
    }
  } else {
    // When hidden / device locked, OS automatically releases wake lock
    isActive.value = false
    // If already past expiry while hidden
    if (targetExpiryTimestamp !== null && Date.now() >= targetExpiryTimestamp) {
      wasScreenOffDuringExpiry = true
    }
  }
}

const ensureVisibilityListener = () => {
  if (isVisibilityListenerAttached || typeof document === 'undefined') return
  isVisibilityListenerAttached = true
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

export const releaseGlobalWakeLock = releaseWakeLock

export function useWakeLock() {
  isSupported.value = checkSupported()
  ensureVisibilityListener()

  return {
    isSupported,
    isActive,
    requestWakeLock,
    releaseWakeLock,
    scheduleWakeLockTimeout,
    clearWakeLockTimeout,
    handleVisibilityChange
  }
}
